<template>
  <div class="shop-category-page">
    <nav_list :index="4"></nav_list>

    <div class="category-wrap">
      <div v-if="pageLoading" class="sk-page">
        <div class="sk-crumbs"></div>
        <div class="sk-toolbar">
          <div class="sk-subcats">
            <div class="sk-pill" v-for="n in 5" :key="n"></div>
          </div>
          <div class="sk-filters">
            <div class="sk-pill sk-pill--sm" v-for="n in 3" :key="n"></div>
          </div>
        </div>
        <div class="sk-product-grid">
          <div class="sk-product-card" v-for="n in 8" :key="n">
            <div class="sk-product-cover"></div>
            <div class="sk-product-body">
              <div class="sk-product-title"></div>
              <div class="sk-product-price"></div>
            </div>
          </div>
        </div>
      </div>

      <template v-else>
        <div class="crumbs-bar">
          <span @click="jump('/shop')">{{ $lt('商城首页') }}</span>
          <i class="el-icon-arrow-right"></i>
          <span>{{ displayCategoryName }}</span>
        </div>

        <div class="toolbar-box">
          <div class="subcats">
            <div
              class="subcat-item"
              :class="{ active: currentSubCat === -1 }"
              @click="switchSubCat(-1)"
            >
              {{ $lt('全部') }}
            </div>
            <div
              class="subcat-item"
              v-for="(cat, index) in subCategories"
              :key="cat.id || index"
              :class="{ active: currentSubCat === index }"
              @click="switchSubCat(index)"
            >
              {{ cat.title }}
            </div>
          </div>

          <div class="filters">
            <div
              class="filter-item"
              v-for="(filter, index) in filters"
              :key="filter.key"
              :class="{ active: currentFilter === index }"
              @click="switchFilter(index)"
            >
              {{ filter.name }}
            </div>
          </div>
        </div>

        <div class="product-box">
          <div v-if="sortedProducts.length" class="product-stage">
            <div class="product-grid" :class="{ 'is-loading': productsLoading }">
              <div
                class="product-card"
                v-for="item in sortedProducts"
                :key="item.id"
                @click="goDetail(item)"
              >
                <div class="product-cover">
                  <img :src="item.image" :alt="item.name">
                  <span v-if="item.avg_scores" class="score-tag">★ {{ item.avg_scores }}</span>
                </div>
                <div class="product-content">
                  <div class="product-title">{{ item.name }}</div>
                  <div class="product-footer">
                    <span class="product-price">¥ {{ item.price }}</span>
                    <span class="product-link">{{ $lt('查看详情') }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="productsLoading" class="skeleton-overlay">
              <div class="skeleton-grid">
                <div v-for="item in sortedProducts.length || pageSize" :key="'skeleton-' + item" class="skeleton-card">
                  <div class="skeleton-cover"></div>
                  <div class="skeleton-content">
                    <div class="skeleton-line skeleton-line--title"></div>
                    <div class="skeleton-line skeleton-line--meta"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else-if="productsLoading" class="skeleton-grid">
            <div v-for="item in pageSize" :key="'skeleton-empty-' + item" class="skeleton-card">
              <div class="skeleton-cover"></div>
              <div class="skeleton-content">
                <div class="skeleton-line skeleton-line--title"></div>
                <div class="skeleton-line skeleton-line--meta"></div>
              </div>
            </div>
          </div>

          <el-empty v-else :description="$lt('当前分类暂无商品')"></el-empty>

          <div v-if="total > pageSize" class="pager-box" :class="{ 'is-loading': productsLoading }">
            <el-pagination
              background
              layout="prev, pager, next"
              :current-page="currentPage"
              :page-size="pageSize"
              :total="total"
              @current-change="changePage"
            />
          </div>
        </div>

        <div v-if="loadFailed" class="error-box">
          <span>{{ $lt('分类数据加载失败，请重试') }}</span>
          <el-button type="primary" size="mini" @click="initPage">{{ $lt('重新加载') }}</el-button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import nav_list from '../index/nav_list.vue';

export default {
  name: 'shop-category',
  components: {
    nav_list,
  },
  data() {
    return {
      pageLoading: true,
      productsLoading: false,
      loadFailed: false,
      categoryId: '',
      categoryName: '',
      categoryCover: '',
      total: 0,
      subCategories: [],
      products: [],
      currentSubCat: -1,
      currentFilter: 0,
      currentPage: 1,
      pageSize: 24,
      filters: [
        { key: 'default', name: this.$lt('综合排序') },
        { key: 'score', name: this.$lt('评分优先') },
        { key: 'price', name: this.$lt('价格最低') },
      ],
    };
  },
  computed: {
    displayCategoryName() {
      return this.getDisplayText(this.categoryName, this.$lt('商品分类'));
    },
    sortedProducts() {
      const list = [...this.products];
      const current = this.filters[this.currentFilter];
      if (!current) {
        return list;
      }
      if (current.key === 'score') {
        return list.sort((a, b) => Number(b.avg_scores || 0) - Number(a.avg_scores || 0));
      }
      if (current.key === 'price') {
        return list.sort((a, b) => Number(a.price || 0) - Number(b.price || 0));
      }
      return list;
    },
  },
  mounted() {
    this.categoryId = this.$route.query.id || this.$route.query.categoryId || '';
    this.categoryName = this.getDisplayText(
      this.$route.query.name || this.$route.query.categoryName,
      this.$lt('商品分类'),
    );
    this.initPage();
  },
  methods: {
    getDisplayText(value, fallback = '') {
      if (value == null) return fallback;
      if (typeof value === 'string' || typeof value === 'number') return String(value);
      if (Array.isArray(value)) {
        return value.map((item) => this.getDisplayText(item, '')).filter(Boolean).join(' / ') || fallback;
      }
      if (typeof value === 'object') {
        return this.getDisplayText(
          value.title || value.name || value.label || value.text || value.desc || value.value,
          fallback,
        );
      }
      return fallback;
    },
    mapProduct(item) {
      return {
        id: item.prod_no,
        name: item.prod_name,
        image: item.prod_img_url && item.prod_img_url.length ? item.prod_img_url[0] : '',
        price: item.b2c_min_price || '0',
        avg_scores: item.avg_scores || '0',
      };
    },
    async initPage() {
      this.pageLoading = true;
      this.loadFailed = false;
      try {
        const res = await this.get('/kkday_product/home_config', {}, 'get', false);
        const category = (res.categories || []).find((item) => String(item.id) === String(this.categoryId));
        if (category) {
          this.categoryName = this.getDisplayText(category.title, '商品分类');
          this.categoryCover = category.sts && category.sts.cover ? category.sts.cover : '';
          this.subCategories = category.children || [];
        }
        await this.loadProducts();
      } catch (error) {
        this.loadFailed = true;
        this.$message.error(this.$lt('加载分类页失败'));
      } finally {
        this.pageLoading = false;
      }
    },
    async loadProducts() {
      if (!this.categoryId) {
        this.products = [];
        return;
      }

      this.productsLoading = true;
      const params = {
        page: this.currentPage,
        pageSize: this.pageSize,
        cate_id: this.categoryId,
      };

      if (this.currentSubCat !== -1 && this.subCategories[this.currentSubCat]) {
        params.cate_sub_id = this.subCategories[this.currentSubCat].id;
      }

      try {
        const res = await this.get('/kkday_product/list', params, 'get', false);
        this.products = (res.list || []).map(this.mapProduct);
        this.total = Number(res.total || res.count || res.total_count || this.products.length || 0);
      } catch (error) {
        this.products = [];
        this.total = 0;
        this.$message.error(this.$lt('加载商品失败'));
      } finally {
        this.productsLoading = false;
      }
    },
    async switchSubCat(index) {
      this.currentSubCat = index;
      this.currentPage = 1;
      await this.loadProducts();
    },
    switchFilter(index) {
      this.currentFilter = index;
    },
    async changePage(page) {
      this.currentPage = page;
      await this.loadProducts();
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    goDetail(item) {
      this.jump('/shop/info', { prod_no: item.id, id: item.id });
    },
  },
};
</script>

<style scoped>
.shop-category-page {
  min-height: 100vh;
  background: #f5f7fb;
  padding-bottom: 40px;
}

.category-wrap {
  width: 1200px;
  margin: 20px auto 0;
}

.crumbs-bar {
  display: flex;
  align-items: center;
  color: #8b95a7;
  font-size: 13px;
  padding: 12px 0;
}

.crumbs-bar span {
  cursor: pointer;
}

.crumbs-bar span:last-child {
  color: #334155;
  cursor: default;
}

.crumbs-bar i {
  margin: 0 8px;
  font-size: 11px;
}

.toolbar-box,
.product-box,
.loading-box,
.error-box {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
}

.toolbar-box {
  margin-top: 0;
  padding: 20px 24px;
}

.subcats,
.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.filters {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #edf1f7;
}

.subcat-item,
.filter-item {
  padding: 10px 18px;
  border-radius: 18px;
  background: #f5f7fb;
  color: #666;
  font-size: 13px;
  cursor: pointer;
}

.subcat-item.active,
.filter-item.active {
  background: #1c5098;
  color: #fff;
}

.product-box {
  margin-top: 20px;
  padding: 24px;
}

.product-stage {
  position: relative;
  min-height: 580px;
}

.product-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  transition: opacity 0.25s ease;
}

.product-grid.is-loading {
  opacity: 0.22;
  pointer-events: none;
}

.product-card {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  background: #fff;
}

.product-cover {
  position: relative;
  height: 220px;
  background: #f3f6fb;
}

.product-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.score-tag {
  position: absolute;
  right: 12px;
  top: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  background: rgba(0, 0, 0, 0.58);
  color: #fff;
  font-size: 12px;
}

.product-content {
  padding: 14px;
}

.product-title {
  color: #333;
  font-size: 15px;
  font-weight: bold;
  line-height: 1.7;
  min-height: 50px;
}

.product-footer {
  margin-top: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.product-price {
  color: #1c5098;
  font-size: 22px;
  font-weight: bold;
}

.product-link {
  color: #8b95a7;
  font-size: 12px;
  position: relative;
  transition: color 0.25s ease;
}

.product-link::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -2px;
  width: 0;
  height: 1px;
  background: #1c5098;
  transition: width 0.3s ease;
}

.product-link:hover {
  color: #1c5098;
}

.product-link:hover::after {
  width: 100%;
}

/* ===== Page Load Fade-in ===== */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.category-wrap > .crumbs-bar {
  animation: fadeInUp 0.5s ease both;
}

.category-wrap > .toolbar-box {
  animation: fadeInUp 0.5s ease 0.08s both;
}

.category-wrap > .product-box {
  animation: fadeInUp 0.5s ease 0.16s both;
}

/* ===== Crumbs Bar Hover ===== */
.crumbs-bar span:not(:last-child) {
  transition: color 0.2s ease;
}

.crumbs-bar span:not(:last-child):hover {
  color: #1c5098;
}

/* ===== Sub-category Tags ===== */
.subcat-item {
  transition: all 0.25s ease;
}

.subcat-item:hover {
  background: #e8edf5;
  color: #1c5098;
}

.subcat-item.active {
  transform: scale(1.05);
}

/* ===== Filter Tags ===== */
.filter-item {
  transition: all 0.25s ease;
}

.filter-item:hover {
  background: #e8edf5;
  color: #1c5098;
}

.filter-item.active {
  transform: scale(1.05);
}

/* ===== Product Card Hover ===== */
.product-card {
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
}

.product-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.12);
}

.product-cover img {
  transition: transform 0.4s ease;
}

.product-card:hover .product-cover img {
  transform: scale(1.08);
}

/* ===== Score Tag Hover ===== */
.score-tag {
  transition: all 0.25s ease;
}

.score-tag:hover {
  transform: scale(1.1);
  background: rgba(0, 0, 0, 0.75);
}

/* ===== Product Price Hover ===== */
.product-price {
  display: inline-block;
  transition: all 0.3s ease;
}

.product-card:hover .product-price {
  color: #0f3d7a;
  transform: scale(1.06);
}

/* ===== Page Skeleton ===== */
@keyframes sk-pulse {
  0% { opacity: 0.4; }
  50% { opacity: 1; }
  100% { opacity: 0.4; }
}

.sk-page {
  animation: sk-pulse 1.8s ease-in-out infinite;
}

.sk-crumbs {
  width: 180px;
  height: 14px;
  border-radius: 4px;
  background: #e2e8f0;
  margin-bottom: 16px;
}

.sk-toolbar {
  background: #fff;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 4px 16px 1px rgb(0 0 0 / 6%);
  margin-bottom: 20px;
}

.sk-subcats,
.sk-filters {
  display: flex;
  gap: 12px;
}

.sk-filters {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #edf1f7;
}

.sk-pill {
  height: 38px;
  width: 80px;
  border-radius: 18px;
  background: #e2e8f0;
}

.sk-pill--sm {
  height: 32px;
  width: 70px;
}

.sk-product-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
}

.sk-product-card {
  border-radius: 10px;
  overflow: hidden;
  background: #f8fafc;
}

.sk-product-cover {
  height: 220px;
  background: linear-gradient(135deg, #e2e8f0 0%, #f1f5f9 100%);
}

.sk-product-body {
  padding: 14px;
}

.sk-product-title {
  height: 50px;
  border-radius: 6px;
  background: #e2e8f0;
}

.sk-product-price {
  height: 22px;
  width: 60%;
  margin-top: 14px;
  border-radius: 4px;
  background: #e2e8f0;
}

.error-box {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
}

.pager-box {
  padding-top: 28px;
  text-align: right;
  transition: opacity 0.25s ease;
}

.pager-box.is-loading {
  opacity: 0.7;
}

.skeleton-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 18px;
  min-height: 580px;
}

.skeleton-overlay {
  position: absolute;
  inset: 0;
  z-index: 2;
}

.skeleton-card {
  border: 1px solid #edf1f7;
  border-radius: 10px;
  overflow: hidden;
  background: #fff;
}

.skeleton-cover,
.skeleton-line {
  position: relative;
  overflow: hidden;
  background: #eef3fb;
}

.skeleton-cover::after,
.skeleton-line::after {
  content: '';
  position: absolute;
  inset: 0;
  transform: translateX(-100%);
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.75), transparent);
  animation: skeleton-loading 1.4s infinite;
}

.skeleton-cover {
  height: 220px;
}

.skeleton-content {
  padding: 14px;
}

.skeleton-line {
  height: 14px;
  border-radius: 8px;
}

.skeleton-line--title {
  height: 42px;
  border-radius: 10px;
}

.skeleton-line--meta {
  width: 65%;
  margin-top: 14px;
}

@keyframes skeleton-loading {
  100% {
    transform: translateX(100%);
  }
}

@media (max-width: 1260px) {
  .category-wrap {
    width: 96%;
  }

  .product-grid,
  .skeleton-grid,
  .sk-product-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 900px) {
  .product-grid,
  .skeleton-grid,
  .sk-product-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}
</style>



// WEBPACK FOOTER //
// src/pages/shop/category.vue